import {
  OnInit,
  inject,
  Component,
  ViewChild,
  DestroyRef,
  AfterViewInit,
  ViewContainerRef,
  ChangeDetectionStrategy,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { Store } from '@ngrx/store';
import { IconProp } from '@fortawesome/fontawesome-svg-core';

import {
  SuiDialogConfig,
  DialogCloseReason,
  SuiDialogTabsComponent,
  SuiDialogLeftHeaderSlot,
  SuiDialogLeftContentSlot,
  SuiDialogRightHeaderSlot,
  SuiDialogRightContentSlot,
  SuiDialogRightFooterSlot,
} from '@sui/dialog';

import { UserAccessLevel } from 'src/app/core/services/user.service';
import { UtilsService } from 'src/app/core/services/utils.service';

import { WorkspaceSettingsComponent } from '../../components/workspace-settings/workspace-settings.component';
import { AuditTrailComponent } from '../../../shared/components/audit-trail/audit-trail.component';
import { ObjectPermissionsComponent } from '../../../objects/components/object-permissions/object-permissions.component';

import { Workspace } from 'src/app/core/models/core.models';
import { getIconForDataObject } from 'src/app/shared/utils/object-utils';
import { TabConfig, SearchableDialog } from 'src/app/shared/utils';
import {
  TabDialogBase,
  TabDialogDataBase,
} from 'src/app/shared/abstract/tab-dialog-base';

import {
  injectCreateWorkspaceMutation,
  injectUpdateWorkspaceMutation,
} from 'src/app/api/queries/workspace.queries';

import { AppState } from 'src/app/core/state/app.state';
import { selectUser } from 'src/app/core/state';

import { tap } from 'rxjs';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { SearchInputComponent } from '../../../shared/components/search-input/search-input.component';
import { DialogTabNavComponent } from '../../../shared/components/dialog-tab-nav/dialog-tab-nav.component';
import { SuiButtonComponent } from '@sui/button';
import { TranslatePipe } from '@ngx-translate/core';

interface WorkspaceDialogInput extends TabDialogDataBase {
  workspace?: Workspace;
}

/** Close data: the submitted form plus `saved`, the in-flight request (SavedCloseData). */
interface WorkspaceDialogOutput {
  saved?: Promise<unknown>;
  [field: string]: unknown;
}

@Component({
  selector: 'app-workspace-editor-dialog',
  templateUrl: './workspace-editor-dialog.component.html',
  styleUrl: './workspace-editor-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    SuiDialogTabsComponent,
    SuiDialogLeftHeaderSlot,
    FaIconComponent,
    SuiDialogLeftContentSlot,
    SearchInputComponent,
    DialogTabNavComponent,
    SuiDialogRightHeaderSlot,
    SuiButtonComponent,
    SuiDialogRightContentSlot,
    SuiDialogRightFooterSlot,
    TranslatePipe,
  ],
})
export class WorkspaceEditorDialogComponent
  extends TabDialogBase<WorkspaceDialogInput, WorkspaceDialogOutput>
  implements SearchableDialog, OnInit, AfterViewInit
{
  static dialogConfig: Partial<SuiDialogConfig> = {
    width: '50vw',
    minWidth: 800,
    maxWidth: 1020,
  };
  protected destroyRef = inject(DestroyRef);
  private store = inject(Store<AppState>);
  private utilsService = inject(UtilsService);

  private createWorkspaceMutation = injectCreateWorkspaceMutation();
  private updateWorkspaceMutation = injectUpdateWorkspaceMutation();

  @ViewChild('tabHost', { read: ViewContainerRef })
  protected override tabHost!: ViewContainerRef;

  // * Search Manager
  private tabSearch = this.enableTabSearch();
  onSearchInput = this.tabSearch.onSearchInput;

  // * UI state
  objectActionTitle = '';
  objectNameTitle = '';
  objectIcon: IconProp = ['fas', 'folder'];
  objectActionIcon: IconProp = ['fas', 'cog'];

  get isUpdateMode(): boolean {
    return !!this.dialogData.workspace;
  }

  get workspace(): Workspace | undefined {
    return this.dialogData.workspace;
  }

  get searchString$() {
    return this.tabSearch.searchString$;
  }

  get visibleTabs(): TabConfig[] {
    return this.tabSearch.getVisibleTabs(this.tabs);
  }

  ngOnInit(): void {
    this.setupUIState();
    this.setupTabs();
  }

  ngAfterViewInit(): void {
    this.initializeBase();
    this.tabSearch.setupAutoSwitch(this.tabs);
  }

  private setupUIState(): void {
    if (this.isUpdateMode && this.workspace) {
      this.objectActionTitle = this.translateService.instant(
        'popup.action-title.edit-workspace-settings',
      );
      this.objectNameTitle = this.workspace.title;
      this.objectIcon = getIconForDataObject(
        this.workspace.object_class_id,
        this.workspace.object_type_id,
      );
    } else {
      this.objectActionTitle = this.translateService.instant(
        'popup.action-title.create-workspace',
      );
      this.objectNameTitle = this.translateService.instant(
        'popup.object-title.new-workspace',
      );
      this.objectIcon = ['fas', 'folder'];
    }
  }

  private setupTabs(): void {
    // General tab (always present)
    this.tabs = [
      {
        id: 'general',
        label: this.translateService.instant(
          'workspace-editor-popup.tab.general',
        ),
        component: WorkspaceSettingsComponent,
        inputs: {
          workspace: this.workspace,
          searchString$: this.searchString$,
        },
      },
    ];

    // * additional Tabs only in Update Mode
    if (this.isUpdateMode && this.workspace) {
      this.tabs.push(
        {
          id: 'permissions',
          label: this.translateService.instant(
            'workspace-editor-popup.tab.permissions',
          ),
          component: ObjectPermissionsComponent,
          inputs: {
            searchString$: this.searchString$,
            objects: [this.workspace],
            companyId: this.workspace.company_id,
            allowOwnerChange: true,
          },
          objectPermissions: ['can_manage'],
        },
        {
          id: 'audit-trail',
          label: this.translateService.instant(
            'workspace-editor.tab.audit-trail',
          ),
          userAccessLevel: UserAccessLevel.CLIENT_ADMIN,
          component: AuditTrailComponent,
          inputs: {
            searchString$: this.searchString$,
            object: this.workspace,
            hasOwner: true,
            timeCreatedKey: 'time_created',
            timeModifiedKey: 'time_modified',
          },
          objectPermissions: ['can_manage'],
        },
      );

      this.store
        .select(selectUser)
        .pipe(
          takeUntilDestroyed(this.destroyRef),
          tap((user) =>
            this.filterTabsByObjectPermissions(this.workspace, user),
          ),
        )
        .subscribe();
    }
  }

  /** Waits for the save (WAP-1976): closes on success, keeps the form with the refusal in the band. */
  protected override performSave(formData: unknown): Promise<void> {
    // * the hook's onSuccess patches the cached list on the real response; the close
    // * event keeps `saved` for the opener's row refresh, already resolved by then
    // * dotted i18n control names (title_i18n.language_id_N) must become a nested
    // * title_i18n object before hitting the wire - PHP mangles dots in param names
    const wireData = this.utilsService.convertFormDataI18nKeys(
      formData as Record<string, any>,
    );
    const isUpdate = this.isUpdateMode && !!this.workspace;
    const data = wireData as {
      title: string;
      description: string;
      title_i18n?: Record<string, string>;
    };
    const saved = isUpdate
      ? this.updateWorkspaceMutation.mutateAsync({
          id: this.workspace.id,
          workspace: wireData,
        })
      : this.createWorkspaceMutation.mutateAsync({
          title: data.title,
          description: data.description,
          title_i18n: data.title_i18n,
        });
    return this.closeOnceSaved(
      saved,
      {
        reason: isUpdate ? DialogCloseReason.Update : DialogCloseReason.Save,
        data: { ...(formData as object), saved },
      },
      this.translateService.instant(
        'workspace-editor-popup.msg.the-workspace-could-not-be-saved',
      ),
    );
  }

  save(): void {
    if (!this.validateAndShow()) {
      return;
    }

    // * all valid, proceed with save
    const formData = this.tabManager.combineValues();
    void this.performSave(formData);
  }

  async cancel(): Promise<void> {
    await this.dirtyHandler.tryClose({
      reason: DialogCloseReason.Cancel,
    });
  }
}
